import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from '../../hooks/useFetch';
import Page from '../../components/Page';
import PageTitle from '../../components/PageTitle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const usd = c => (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function CapitalPreset({ setAuth }) {
  const { t } = useTranslation();
  const { fetchResource, showAlert, setShowAlert, alertMessage, setAlertMessage } = useFetch({ setAuth });
  const [form, setForm] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loanBalance, setLoanBalance] = useState(null);
  const [ratePct, setRatePct] = useState(null);

  async function load() {
    const loaded = await fetchResource({ method: 'GET', resource: 'cash-forecast/capital' });
    if (!loaded || loaded.error) return;
    const { preset, accounts: loadedAccounts, loanBalance: loadedLoanBalance } = loaded;
    setUpdatedAt(preset?.updated_at ?? null);
    setAccounts(loadedAccounts ?? []);
    setLoanBalance(loadedLoanBalance ?? null);
    setRatePct(preset ? preset.rate_bps / 100 : null);
    setForm(preset ? { pct: String(preset.rate_bps / 100), debit: String(preset.debit_bank_account_id), start: preset.loan_start_date,
      active: preset.active,
      loanBalanceAmount: preset.loan_balance_cents != null ? String(preset.loan_balance_cents / 100) : '',
      loanBalanceAsOf: preset.loan_balance_as_of_date ?? '' }
      : { pct: '6', debit: '', start: '', active: true, loanBalanceAmount: '', loanBalanceAsOf: '' });
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    const body = { rate_bps: Math.round(Number(form.pct) * 100), debit_bank_account_id: Number(form.debit), loan_start_date: form.start,
      active: form.active,
      loan_balance_cents: form.loanBalanceAmount === '' ? null : Math.round(Number(form.loanBalanceAmount) * 100),
      loan_balance_as_of_date: form.loanBalanceAsOf === '' ? null : form.loanBalanceAsOf };
    const { error } = await fetchResource({ method: 'PUT', resource: 'cash-forecast/capital', body, blocking: true });
    setAlertMessage({ message: error ? error : t('capital.saved'), type: error ? 'error' : 'success' });
    setShowAlert(true);
    if (!error) await load();
  }

  if (!form) return showAlert ? <Page><Alert severity={alertMessage.type || 'info'}>{alertMessage.message}</Alert></Page> : null;
  const set = k => e => setForm({ ...form, [k]: k === 'active' ? e.target.checked : e.target.value });
  return (
    <Page>
      <PageTitle>{t('capital.title')}</PageTitle>
      {showAlert && <Alert severity={alertMessage.type || 'info'}>{alertMessage.message}</Alert>}
      <Box sx={{ display: 'grid', gap: 2, maxWidth: 420, mx: 'auto' }}>
        <TextField label={t('capital.rate')} value={form.pct} onChange={set('pct')} inputProps={{ inputMode: 'decimal' }} />
        {accounts.length > 0
          ? <TextField select label={t('capital.debitAccount')} value={form.debit} onChange={set('debit')}>
              {accounts.map(a => <MenuItem key={a.id} value={String(a.id)}>{`${a.nickname} …${a.last4}`}</MenuItem>)}
            </TextField>
          : <TextField label={t('capital.debitAccount')} value={form.debit} onChange={set('debit')} inputProps={{ inputMode: 'numeric' }} />}
        <TextField label={t('capital.loanStart')} type="date" value={form.start} onChange={set('start')} InputLabelProps={{ shrink: true }} />
        <FormControlLabel control={<Switch checked={form.active} onChange={set('active')} />} label={t('capital.active')} />
        {ratePct != null && <Typography variant="body2">{t('capital.fromSales', { pct: ratePct })}</Typography>}
        {updatedAt && <Typography variant="body2">{t('capital.updated', { date: new Date(updatedAt).toLocaleDateString() })}</Typography>}
        <Typography variant="h6">{t('capital.loanBalanceTitle')}</Typography>
        <TextField label={t('capital.loanBalanceAmount')} value={form.loanBalanceAmount} onChange={set('loanBalanceAmount')} inputProps={{ inputMode: 'decimal' }} />
        <TextField label={t('capital.loanBalanceAsOf')} type="date" value={form.loanBalanceAsOf} onChange={set('loanBalanceAsOf')} InputLabelProps={{ shrink: true }} />
        {loanBalance && (
          <Typography variant="body2">
            {loanBalance.lastDebitDate
              ? t('capital.loanBalanceRemaining', { amount: usd(loanBalance.remainingCents), date: new Date(loanBalance.lastDebitDate).toLocaleDateString() })
              : t('capital.loanBalanceRemainingNoDebits', { amount: usd(loanBalance.remainingCents) })}
          </Typography>
        )}
        <Button variant="contained" onClick={save}>{t('capital.save')}</Button>
      </Box>
    </Page>
  );
}
